
import React from 'react';
import { ArrowRight, Command, Sparkles } from 'lucide-react';
import { useLanguage } from '../i18nContext';

interface GuestGateModalProps {
  onSignUp: () => void;
  onSignIn: () => void;
}

export default function GuestGateModal({ onSignUp, onSignIn }: GuestGateModalProps) {
  const { t } = useLanguage();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 isolate">
      {/* 
        The Backdrop: 
        1. Reduced blur from 12px to 8px for higher background clarity.
        2. Reduced opacity from 45% to 35% (bg-white/35).
        3. transform-gpu and isolated layer ensure no hover-induced flickering.
      */}
      <div 
        className="absolute inset-0 bg-white/35 backdrop-blur-[8px] transform-gpu pointer-events-auto transition-none"
        style={{ backfaceVisibility: 'hidden', perspective: 1000 }}
      >
        {/* Subtle radial light to keep focus on the center modal */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(255,255,255,0.25)_100%)] pointer-events-none"></div>
      </div>

      {/* Modal Content Container */}
      <div className="relative w-full max-w-lg bg-white rounded-modal shadow-modal-heavy overflow-hidden border border-white animate-in zoom-in-95 fade-in duration-500 z-10">
        
        {/* Decorative Gradient Header */}
        <div className="absolute top-0 inset-x-0 h-32 bg-gradient-to-b from-surface to-transparent pointer-events-none opacity-50"></div>
        <div className="absolute top-[-50px] left-[-50px] w-48 h-48 bg-brand-200/40 rounded-full blur-[60px] pointer-events-none"></div>
        <div className="absolute top-[-50px] right-[-50px] w-48 h-48 bg-prestige-300/30 rounded-full blur-[60px] pointer-events-none"></div>

        <div className="relative p-10 md:p-12 text-center flex flex-col items-center">
            {/* Logo/Icon */}
            <div className="w-20 h-20 bg-white rounded-card mb-8 flex items-center justify-center border border-surface-border shadow-sm relative group">
                 <div className="absolute inset-0 bg-gradient-to-br from-surface to-white rounded-card"></div>
                 <div className="relative w-10 h-10 bg-brand-900 rounded-xl flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-500">
                    <Command size={24} strokeWidth={2.5} />
                 </div>
                 <div className="absolute -top-3 -right-3 text-brand-500 drop-shadow-sm">
                    <Sparkles size={20} fill="currentColor" className="animate-pulse" />
                 </div>
            </div>

            <h2 className="text-3xl font-serif font-bold text-brand-900 mb-4 tracking-tight leading-tight">
              {t.gateModal.title}
            </h2>
            
            <p className="text-ink-secondary text-base leading-relaxed mb-10 max-w-sm mx-auto font-medium opacity-80">
              {t.gateModal.subtitle}
            </p>

            <div className="space-y-4 w-full">
                <button 
                    onClick={onSignUp}
                    className="w-full relative overflow-hidden bg-brand-900 hover:bg-brand-800 text-white h-14 rounded-2xl text-base font-bold shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300 group flex items-center justify-center gap-3"
                >
                    <div className="absolute inset-0 bg-noise opacity-10 mix-blend-overlay"></div>
                    <span className="relative z-10">{t.gateModal.createAccount}</span>
                    <ArrowRight size={18} className="relative z-10 opacity-90 group-hover:translate-x-1 transition-transform stroke-[2.5]" />
                </button>

                <button 
                    onClick={onSignIn}
                    className="w-full py-2 text-sm font-bold text-ink-dark hover:text-brand-900 transition-colors flex items-center justify-center gap-1.5"
                >
                    {t.gateModal.orSignIn}
                </button>
            </div>
        </div>
        
        {/* Subtle Bottom Accent */}
        <div className="h-1.5 w-full bg-gradient-to-r from-transparent via-surface-border to-transparent opacity-30"></div>
      </div>
    </div>
  );
}
